(function () {
  "use strict";

  if (window.MarineMaxCoveoFilterUrlState) return;

  const COVEO_URL_PARAM_SORT = "sort";
  const COVEO_URL_PARAM_PAGE = "page";
  const COVEO_URL_LEGACY_PARAM_SORT = "coveo-sort";
  const COVEO_URL_LEGACY_PARAM_PAGE = "coveo-page";
  const COVEO_URL_LEGACY_FACET_PREFIX = "coveo-f.";
  const COVEO_URL_LEGACY_RANGE_PREFIX = "coveo-rf.";

  const REQUIRED_DEPENDENCIES = Object.freeze([
    "conditionValuesRepresentAll",
    "facetIdsMatchCondition",
    "getCanonicalConditionOptionLabel",
    "getCoveoCanonicalFacetIdForFriendlyUrlKey",
    "getCoveoFriendlyUrlFacetKey",
    "getCurrentHref",
    "getOrigin",
    "isCoveoFriendlyUrlRangeKey",
    "isSupportedSort",
    "replaceUrl",
  ]);

  function createCoveoFilterUrlState(dependencies) {
    const deps = dependencies || {};
    const missingDependencies = REQUIRED_DEPENDENCIES.filter(function (name) {
      return typeof deps[name] !== "function";
    });
    if (missingDependencies.length) {
      throw new Error(
        `[Coveo Filter URL State] Missing dependencies: ${missingDependencies.join(", ")}`,
      );
    }

    const {
      conditionValuesRepresentAll,
      facetIdsMatchCondition,
      getCanonicalConditionOptionLabel,
      getCoveoCanonicalFacetIdForFriendlyUrlKey,
      getCoveoFriendlyUrlFacetKey,
      getCurrentHref,
      getOrigin,
      isCoveoFriendlyUrlRangeKey,
      isSupportedSort,
      replaceUrl,
    } = deps;
    const defaultConditionValues = Array.isArray(deps.defaultConditionValues)
      ? Array.from(
          new Set(
            deps.defaultConditionValues
              .map(getCanonicalConditionOptionLabel)
              .filter(Boolean),
          ),
        )
      : [];

    function createEmptyCoveoUrlState() {
      return {
        query: "",
        sort: null,
        page: null,
        facetValues: new Map(),
        facetRanges: new Map(),
      };
    }

    function normalizeCoveoUrlFacetId(raw) {
      return String(raw || "").trim().toLowerCase();
    }

    function isInternalCoveoUrlFacet(facetId) {
      return normalizeCoveoUrlFacetId(facetId) === "permanentid";
    }

    function parseCoveoUrlRangeValue(raw) {
      const parts = String(raw || "").split("..");
      if (parts.length !== 2) return null;
      const lo = parseFloat(parts[0]);
      const hi = parseFloat(parts[1]);
      if (!Number.isFinite(lo) || !Number.isFinite(hi)) return null;
      return {
        lo: Math.min(lo, hi),
        hi: Math.max(lo, hi),
      };
    }

    function formatCoveoUrlRangeValue(range) {
      if (!range) return "";
      const lo = Number(range.lo);
      const hi = Number(range.hi);
      if (!Number.isFinite(lo) || !Number.isFinite(hi)) return "";
      return `${lo}..${hi}`;
    }

    function conditionValuesRepresentNoFilter(facetId, values) {
      if (!facetIdsMatchCondition(facetId) || !Array.isArray(values)) {
        return false;
      }
      return values.some(function (value) {
        return String(value || "").trim().toLowerCase() === "all";
      });
    }

    function normalizeConditionFacetValues(facetValues) {
      if (!(facetValues instanceof Map)) return facetValues;
      const conditionValues = [];
      Array.from(facetValues.keys()).forEach(function (facetId) {
        if (!facetIdsMatchCondition(facetId)) return;
        const values = facetValues.get(facetId);
        if (Array.isArray(values)) conditionValues.push(...values);
        facetValues.delete(facetId);
      });
      if (!conditionValues.length) return facetValues;
      if (
        conditionValuesRepresentNoFilter(
          getCoveoCanonicalFacetIdForFriendlyUrlKey("condition") ||
            "condition",
          conditionValues,
        )
      ) {
        return facetValues;
      }

      const normalizedValues = Array.from(
        new Set(
          conditionValues
            .map(getCanonicalConditionOptionLabel)
            .filter(Boolean),
        ),
      );
      if (!normalizedValues.length) return facetValues;
      const canonicalFacetId =
        getCoveoCanonicalFacetIdForFriendlyUrlKey("condition") ||
        "condition";
      const storageKey =
        getCoveoFriendlyUrlFacetKey(canonicalFacetId) || "condition";
      facetValues.set(storageKey, normalizedValues);
      return facetValues;
    }

    function readCoveoUrlState(sourceHref) {
      let url;
      try {
        url = sourceHref
          ? new URL(sourceHref, getOrigin())
          : new URL(getCurrentHref());
      } catch (_) {
        return createEmptyCoveoUrlState();
      }

      const facetValues = new Map();
      const facetRanges = new Map();
      url.searchParams.forEach(function (value, key) {
        if (key.startsWith(COVEO_URL_LEGACY_FACET_PREFIX)) {
          const facetId = normalizeCoveoUrlFacetId(
            key.slice(COVEO_URL_LEGACY_FACET_PREFIX.length),
          );
          const normalizedValue = String(value || "").trim();
          if (
            !facetId ||
            isInternalCoveoUrlFacet(facetId) ||
            !normalizedValue
          ) {
            return;
          }
          const nextValues = facetValues.get(facetId) || [];
          if (!nextValues.includes(normalizedValue)) {
            nextValues.push(normalizedValue);
          }
          facetValues.set(facetId, nextValues);
          return;
        }

        if (key.startsWith(COVEO_URL_LEGACY_RANGE_PREFIX)) {
          const facetId = normalizeCoveoUrlFacetId(
            key.slice(COVEO_URL_LEGACY_RANGE_PREFIX.length),
          );
          const parsedRange = parseCoveoUrlRangeValue(value);
          if (facetId && parsedRange) {
            facetRanges.set(facetId, parsedRange);
          }
          return;
        }

        const friendlyFacetId =
          getCoveoCanonicalFacetIdForFriendlyUrlKey(key);
        if (!friendlyFacetId) return;
        if (isCoveoFriendlyUrlRangeKey(key)) {
          const parsedRange = parseCoveoUrlRangeValue(value);
          if (parsedRange) {
            facetRanges.set(String(key).trim().toLowerCase(), parsedRange);
          }
          return;
        }

        const normalizedValue = String(value || "").trim();
        if (!normalizedValue) return;
        const friendlyKey = String(key).trim().toLowerCase();
        const nextValues = facetValues.get(friendlyKey) || [];
        if (!nextValues.includes(normalizedValue)) {
          nextValues.push(normalizedValue);
        }
        facetValues.set(friendlyKey, nextValues);
      });

      // "All" is a UI-only state. Collapse aliases to the canonical Condition
      // key while preserving every explicitly selected value.
      const conditionAllSelected = Array.from(facetValues).some(
        function ([facetId, values]) {
          return conditionValuesRepresentNoFilter(facetId, values);
        }
      );
      normalizeConditionFacetValues(facetValues);

      const rawSort = String(
        url.searchParams.get(COVEO_URL_PARAM_SORT) ||
          url.searchParams.get(COVEO_URL_LEGACY_PARAM_SORT) ||
          "",
      ).trim();
      const rawPage = parseInt(
        String(
          url.searchParams.get(COVEO_URL_PARAM_PAGE) ||
            url.searchParams.get(COVEO_URL_LEGACY_PARAM_PAGE) ||
            "",
        ),
        10,
      );

      return {
        ...(conditionAllSelected ? { conditionAllSelected: true } : {}),
        query: String(url.searchParams.get("q") || "").trim(),
        sort: isSupportedSort(rawSort) ? rawSort : null,
        page: Number.isFinite(rawPage) && rawPage > 1 ? rawPage : null,
        facetValues,
        facetRanges,
      };
    }

    function readInitialCoveoUrlState(sourceHref) {
      const state = readCoveoUrlState(sourceHref);
      let url;
      try {
        url = new URL(sourceHref || getCurrentHref(), getOrigin());
      } catch (_) {
        return state;
      }

      // Only a fresh visit receives defaults. Even an empty Condition or All
      // parameter represents an explicit choice that must survive reloads.
      const hasExplicitCondition = Array.from(url.searchParams.keys()).some(
        function (key) {
          const normalizedKey = normalizeCoveoUrlFacetId(key);
          const facetId = normalizedKey.startsWith(COVEO_URL_LEGACY_FACET_PREFIX)
            ? normalizedKey.slice(COVEO_URL_LEGACY_FACET_PREFIX.length)
            : getCoveoCanonicalFacetIdForFriendlyUrlKey(normalizedKey);
          return facetIdsMatchCondition(facetId);
        },
      );
      if (!hasExplicitCondition && defaultConditionValues.length) {
        state.facetValues.set("condition", defaultConditionValues.slice());
      }
      return state;
    }

    function hasCoveoUrlState(state) {
      return !!(
        state &&
        (state.sort ||
          state.page ||
          (state.facetValues instanceof Map && state.facetValues.size > 0) ||
          (state.facetRanges instanceof Map && state.facetRanges.size > 0))
      );
    }

    function isManagedCoveoUrlParamKey(key) {
      const normalized = String(key || "").trim().toLowerCase();
      return !!(
        normalized === COVEO_URL_PARAM_SORT ||
        normalized === COVEO_URL_PARAM_PAGE ||
        normalized === COVEO_URL_LEGACY_PARAM_SORT ||
        normalized === COVEO_URL_LEGACY_PARAM_PAGE ||
        normalized.startsWith(COVEO_URL_LEGACY_FACET_PREFIX) ||
        normalized.startsWith(COVEO_URL_LEGACY_RANGE_PREFIX) ||
        getCoveoCanonicalFacetIdForFriendlyUrlKey(normalized)
      );
    }

    function getCoveoUrlStateLookupKeys(facetId) {
      const normalizedFacetId = normalizeCoveoUrlFacetId(facetId);
      const keys = [];
      if (normalizedFacetId) keys.push(normalizedFacetId);
      const friendlyKey = getCoveoFriendlyUrlFacetKey(normalizedFacetId);
      if (friendlyKey && !keys.includes(friendlyKey)) {
        keys.push(friendlyKey);
      }
      // Keep old shared links readable while requests use the indexed year1 field.
      if (friendlyKey === "year") {
        ["year1", "year_iiq", "year"].forEach(function (key) {
          if (!keys.includes(key)) keys.push(key);
        });
      }
      return keys;
    }

    function buildCoveoUrlStateHref(snapshot, sourceHref) {
      const current = snapshot || {};
      const currentHref = String(sourceHref || getCurrentHref() || "");
      if (current.pendingInitialState) return currentHref;

      let url;
      try {
        url = new URL(currentHref);
      } catch (_) {
        return "";
      }

      const managedKeys = [];
      url.searchParams.forEach(function (_value, key) {
        if (isManagedCoveoUrlParamKey(key)) {
          managedKeys.push(key);
        }
      });
      managedKeys.forEach(function (key) {
        url.searchParams.delete(key);
      });

      const query = String(current.query || "").trim();
      if (query) {
        url.searchParams.set("q", query);
      } else {
        url.searchParams.delete("q");
      }

      const currentSort = String(current.sort || "").trim();
      const defaultSort = String(current.defaultSort || "relevance").trim();
      if (currentSort && currentSort !== defaultSort) {
        url.searchParams.set(COVEO_URL_PARAM_SORT, currentSort);
      } else {
        url.searchParams.delete(COVEO_URL_PARAM_SORT);
      }

      const currentPage = current.page;
      if (Number.isFinite(currentPage) && currentPage > 1) {
        url.searchParams.set(COVEO_URL_PARAM_PAGE, String(currentPage));
      } else {
        url.searchParams.delete(COVEO_URL_PARAM_PAGE);
      }

      if (current.facetValues instanceof Map) {
        const serializableFacetValues = normalizeConditionFacetValues(
          new Map(current.facetValues),
        );
        serializableFacetValues.forEach(function (values, facetId) {
          if (!Array.isArray(values)) return;
          const friendlyKey = getCoveoFriendlyUrlFacetKey(facetId);
          if (conditionValuesRepresentNoFilter(facetId, values)) {
            return;
          }
          values.forEach(function (value) {
            if (friendlyKey) {
              url.searchParams.append(friendlyKey, value);
            } else {
              url.searchParams.append(
                `${COVEO_URL_LEGACY_FACET_PREFIX}${facetId}`,
                value,
              );
            }
          });
        });
      }

      // Without a marker a cleared selection would look like a fresh visit
      // and receive the defaults again when reloaded or restored from history.
      if (current.preserveEmptyCondition && !url.searchParams.has("condition")) {
        url.searchParams.set(
          "condition",
          current.conditionAllSelected === false ? "" : "All",
        );
      }

      if (current.facetRanges instanceof Map) {
        current.facetRanges.forEach(function (range, facetId) {
          const encodedRange = formatCoveoUrlRangeValue(range);
          if (!encodedRange) return;
          const friendlyKey = getCoveoFriendlyUrlFacetKey(facetId);
          if (friendlyKey && isCoveoFriendlyUrlRangeKey(friendlyKey)) {
            url.searchParams.set(friendlyKey, encodedRange);
          } else {
            url.searchParams.set(
              `${COVEO_URL_LEGACY_RANGE_PREFIX}${facetId}`,
              encodedRange,
            );
          }
        });
      }

      return url.toString();
    }

    function syncCoveoStateToUrl(snapshot) {
      if (snapshot?.pendingInitialState) return false;
      const currentHref = String(getCurrentHref() || "");
      const nextHref = buildCoveoUrlStateHref(snapshot, currentHref);
      if (!nextHref || nextHref === currentHref) return false;
      replaceUrl(nextHref);
      return true;
    }

    return Object.freeze({
      COVEO_URL_LEGACY_FACET_PREFIX,
      COVEO_URL_LEGACY_PARAM_PAGE,
      COVEO_URL_LEGACY_PARAM_SORT,
      COVEO_URL_LEGACY_RANGE_PREFIX,
      COVEO_URL_PARAM_PAGE,
      COVEO_URL_PARAM_SORT,
      buildCoveoUrlStateHref,
      formatCoveoUrlRangeValue,
      getCoveoUrlStateLookupKeys,
      hasCoveoUrlState,
      isInternalCoveoUrlFacet,
      isManagedCoveoUrlParamKey,
      normalizeCoveoUrlFacetId,
      parseCoveoUrlRangeValue,
      readCoveoUrlState,
      readInitialCoveoUrlState,
      syncCoveoStateToUrl,
    });
  }

  Object.defineProperty(window, "MarineMaxCoveoFilterUrlState", {
    configurable: false,
    enumerable: false,
    value: Object.freeze({
      create: createCoveoFilterUrlState,
    }),
    writable: false,
  });
})();
